// @ts-check

/**
 * Mapbox layer specifications and styling utilities.
 * Creates layer configurations for islands, cultural regions, and migration routes.
 */

/**
 * Create a Mapbox layer spec for island point markers.
 * @param {string} sourceId - The ID of the GeoJSON source
 * @returns {import('mapbox-gl').AnyLayer} Mapbox circle layer configuration
 */
export function createIslandLayer(sourceId) {
  return {
    id: "islands-layer",
    type: "circle",
    source: sourceId,
    paint: {
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 2, 4, 8, 8],
      "circle-color": "#4264fb",
      "circle-stroke-width": 2,
      "circle-stroke-color": "#ffffff",
      "circle-opacity": 0.9
    }
  };
}

/**
 * Create a Mapbox symbol layer for island labels.
 * Uses Mapbox's native label rendering with zoom-based visibility and collision detection.
 *
 * @see {@link https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#symbol|Mapbox Symbol Layer Documentation}
 * @see {@link https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#layout-symbol-property|Symbol Layout Properties}
 * @see {@link https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#paint-symbol-property|Symbol Paint Properties}
 *
 * @param {string} sourceId - The ID of the GeoJSON source
 * @returns {import('mapbox-gl').AnyLayer} Mapbox symbol layer configuration
 */
export function createIslandLabelLayer(sourceId) {
  return {
    id: "islands-labels",
    type: "symbol",
    source: sourceId,
    layout: {
      // Text content: tries name, Name, place_name properties, falls back to empty string
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/expressions/#coalesce
      "text-field": [
        "coalesce",
        ["get", "name"],
        ["get", "Name"],
        ["get", "place_name"],
        ""
      ],
      // Font stack: semibold for solid, grounded feel (distinct from italic route labels)
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#layout-symbol-text-font
      "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
      // Text size scales with zoom: 12px at zoom 2, 14px at zoom 4, 16px at zoom 6, 18px at zoom 10
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/expressions/#interpolate
      "text-size": [
        "interpolate",
        ["linear"],
        ["zoom"],
        2,
        12, // zoom 2: 12px (larger for visibility at default zoom ~4)
        4,
        14, // zoom 4: 14px (default zoom level)
        6,
        16, // zoom 6: 16px
        10,
        18 // zoom 10: 18px
      ],
      // Offset label 1.5 units below the point (0 horizontal, 1.5 vertical)
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#layout-symbol-text-offset
      "text-offset": [1.5, 1.5],
      // Anchor point at bottom or right of the text (below or to the right of the point), depending on available space
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#layout-symbol-text-variable-anchor
      "text-variable-anchor": ["bottom", "right"],
      // base on the text-variable-anchor, justify the text
      "text-justify": "auto",
      // Collision detection: labels won't overlap with other labels
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#layout-symbol-text-allow-overlap
      "text-allow-overlap": false,
      // Respect placement of other labels (participates in collision detection)
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#layout-symbol-text-ignore-placement
      "text-ignore-placement": false,
      // Labels are required (always shown when possible) - island labels have priority over region labels
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#layout-symbol-text-optional
      "text-optional": false,
      // Subtle letter spacing for refined appearance
      // See: https://docs.mapbox.com/style-spec/reference/layers/#layout-symbol-text-letter-spacing
      "text-letter-spacing": 0.02
    },
    paint: {
      // Cool off-white with subtle blue tint (distinct from warm route labels)
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#paint-symbol-text-color
      "text-color": "#f0f4f8",
      // Deep ocean blue-gray halo (cooler tone, complements island/water theme)
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#paint-symbol-text-halo-color
      "text-halo-color": "#1a2633",
      // Refined halo width
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#paint-symbol-text-halo-width
      "text-halo-width": 1.5,
      // Crisp halo blur for clean edges
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#paint-symbol-text-halo-blur
      "text-halo-blur": 0.5
    },
    // Labels appear starting at zoom level 2 (matches map minZoom)
    // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#minzoom
    minzoom: 2
  };
}

/**
 * Create Mapbox layer specs for cultural region polygons (fill + outline).
 * @param {string} sourceId - The ID of the GeoJSON source
 * @returns {{fill: import('mapbox-gl').AnyLayer, outline: import('mapbox-gl').AnyLayer}} Fill and outline layers
 */
export function createCulturalRegionLayers(sourceId) {
  /**
   * @type {import('mapbox-gl').DataDrivenPropertyValueSpecification<string>}
   */
  const regionColors = [
    "match",
    ["get", "place_name"],
    "Micronesia",
    "#ff6b6b",
    "Melanesia",
    "#4ecdc4",
    "Polynesia",
    "#45b7d1",
    "#cccccc"
  ];

  return {
    fill: {
      id: "cultural-regions-fill",
      type: "fill",
      source: sourceId,
      paint: {
        "fill-color": regionColors,
        "fill-opacity": 0.25
      }
    },
    outline: {
      id: "cultural-regions-outline",
      type: "line",
      source: sourceId,
      paint: {
        "line-color": regionColors,
        "line-width": 2,
        "line-opacity": 0.6
      }
    }
  };
}

/**
 * Create a Mapbox symbol layer for polygon labels (usable for any GeoJSON polygon source).
 * Labels are automatically centered within polygons by Mapbox. Ensures one label per polygon feature.
 *
 * @see {@link https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#symbol|Mapbox Symbol Layer Documentation}
 * @see {@link https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#layout-symbol-property|Symbol Layout Properties}
 * @param {string} sourceId - The ID of the GeoJSON source
 * @param {string} layerId - The ID for this label layer
 * @param {string|Array} textField - Property name(s) to use for label text (supports coalesce expression)
 * @returns {import('mapbox-gl').AnyLayer} Mapbox symbol layer configuration
 */
export function createPolygonLabelLayer(
  sourceId,
  layerId,
  textField = ["get", "place_name"]
) {
  // Normalize textField to expression format
  const textFieldExpression =
    /** @type {import('mapbox-gl').DataDrivenPropertyValueSpecification<string>} */ (
      Array.isArray(textField)
        ? textField
        : ["coalesce", ["get", textField], ""]
    );

  return {
    id: layerId,
    type: "symbol",
    source: sourceId,
    layout: {
      // Text content from feature properties
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/expressions/#coalesce
      "text-field": textFieldExpression,
      // Font stack: tries Open Sans Semibold first, falls back to Arial Unicode MS Bold
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#layout-symbol-text-font
      "text-font": ["Open Sans Regular", "Arial Unicode MS Bold"],
      // Text size scales with zoom: larger than island labels
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/expressions/#interpolate
      "text-size": [
        "interpolate",
        ["linear"],
        ["zoom"],
        2,
        16, // zoom 2: 16px (larger than island labels)
        4,
        18, // zoom 4: 18px (default zoom level)
        6,
        20, // zoom 6: 20px
        10,
        24 // zoom 10: 24px
      ],
      // Center labels within polygons (no offset needed)
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#layout-symbol-text-anchor
      "text-anchor": "center",
      // Don't allow overlap - respect collision detection with other labels
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#layout-symbol-text-allow-overlap
      "text-allow-overlap": false,
      // Respect placement of other labels (island labels take precedence in collisions)
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#layout-symbol-text-ignore-placement
      "text-ignore-placement": false,
      // Labels are optional - can be hidden if they collide with island labels
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#layout-symbol-text-optional
      "text-optional": true
    },
    paint: {
      // White text color for visibility on satellite imagery
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#paint-symbol-text-color
      "text-color": "#ffffff",
      // Black halo (outline) around text for better contrast
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#paint-symbol-text-halo-color
      "text-halo-color": "#000000",
      // Halo width in pixels (creates visible outline)
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#paint-symbol-text-halo-width
      "text-halo-width": 2,
      // Halo blur for softer outline edge
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#paint-symbol-text-halo-blur
      "text-halo-blur": 1,
      // Opacity setting for region labels
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#paint-symbol-text-opacity
      "text-opacity": 0.6
    },
    // Labels appear starting at zoom level 2 (matches map minZoom)
    // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#minzoom
    minzoom: 2
  };
}

/**
 * Create a Mapbox layer spec for migration route lines.
 * @param {string} sourceId - The ID of the GeoJSON source
 * @returns {import('mapbox-gl').AnyLayer} Mapbox line layer configuration
 */
export function createMigrationRouteLayer(sourceId) {
  return {
    id: "migration-routes-line",
    type: "line",
    source: sourceId,
    paint: {
      "line-color": "#ffa500",
      "line-width": 3,
      "line-opacity": 0.8
    }
  };
}

/**
 * Create a Mapbox layer spec for migration wave route lines.
 * @param {string} sourceId - The ID of the GeoJSON source
 * @param {string} layerId - Unique layer ID (e.g., "migration-wave-1-routes")
 * @param {string} color - Line color (default: "#ffa500")
 * @returns {import('mapbox-gl').AnyLayer} Mapbox line layer configuration
 */
export function createMigrationWaveRouteLayer(
  sourceId,
  layerId,
  color = "#ffa500"
) {
  return {
    id: layerId,
    type: "line",
    source: sourceId,
    paint: {
      "line-color": color,
      "line-width": 3,
      "line-opacity": 0.8
    }
  };
}

/**
 * Create a Mapbox symbol layer for migration wave arrowheads.
 * Uses a simple triangle/arrow symbol rotated based on route bearing.
 * @param {string} sourceId - The ID of the GeoJSON source
 * @param {string} layerId - Unique layer ID (e.g., "migration-wave-1-arrows")
 * @param {string} color - Arrow color (default: "#ffa500")
 * @returns {import('mapbox-gl').AnyLayer} Mapbox symbol layer configuration
 */
export function createMigrationWaveArrowLayer(
  sourceId,
  layerId,
  color = "#ffa500"
) {
  return {
    id: layerId,
    type: "symbol",
    source: sourceId,
    layout: {
      // Use a simple arrow icon - Mapbox doesn't have built-in arrow icons,
      // so we'll use a text-based arrow symbol (▶) rotated by bearing
      "text-field": "▶",
      "text-size": 32,
      "text-rotate": ["get", "bearing"],
      "text-rotation-alignment": "map",
      "text-anchor": "center",
      "text-allow-overlap": true,
      "text-ignore-placement": true
    },
    paint: {
      "text-color": color
    }
  };
}

/**
 * Create a Mapbox symbol layer for migration wave route labels.
 * Labels are positioned at the midpoint of each route.
 * Uses label_line_1 and label_line_2 properties, stacked vertically.
 * Only shows labels if at least one label line has content.
 * @param {string} sourceId - The ID of the GeoJSON source
 * @param {string} layerId - Unique layer ID (e.g., "migration-wave-1-labels")
 * @returns {import('mapbox-gl').AnyLayer} Mapbox symbol layer configuration
 */
export function createMigrationWaveLabelLayer(sourceId, layerId) {
  return {
    id: layerId,
    type: "symbol",
    source: sourceId,
    layout: {
      // Combine label_line_1 and label_line_2 with newline, only if at least one exists
      "text-field": [
        "case",
        // If both label_line_1 and label_line_2 exist and are not empty
        [
          "all",
          ["!=", ["coalesce", ["get", "label_line_1"], ""], ""],
          ["!=", ["coalesce", ["get", "label_line_2"], ""], ""]
        ],
        [
          "concat",
          ["coalesce", ["get", "label_line_1"], ""],
          "\n",
          ["coalesce", ["get", "label_line_2"], ""]
        ],
        // If only label_line_1 exists and is not empty
        ["!=", ["coalesce", ["get", "label_line_1"], ""], ""],
        ["coalesce", ["get", "label_line_1"], ""],
        // If only label_line_2 exists and is not empty
        ["!=", ["coalesce", ["get", "label_line_2"], ""], ""],
        ["coalesce", ["get", "label_line_2"], ""],
        // Otherwise, empty string (no label)
        ""
      ],
      // Use italic font for route labels to convey movement/flow (distinct from island labels)
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#layout-symbol-text-font
      "text-font": ["Open Sans Italic", "Arial Unicode MS Regular"],
      "text-size": [
        "interpolate",
        ["linear"],
        ["zoom"],
        2,
        12,
        4,
        14,
        6,
        16,
        10,
        18
      ],
      // Data-driven text anchor - reads "text_anchor" property from feature, defaults to "top"
      // Options: "center", "left", "right", "top", "bottom", "top-left", "top-right", "bottom-left", "bottom-right"
      // See: https://docs.mapbox.com/style-spec/reference/layers/#layout-symbol-text-anchor
      "text-anchor": ["coalesce", ["get", "text_anchor"], "top"],
      // Data-driven text offset - reads "text_offset" property from feature, defaults to [0, -1.5]
      // Format: [x, y] where positive x = right, positive y = down (in ems)
      // See: https://docs.mapbox.com/style-spec/reference/layers/#layout-symbol-text-offset
      "text-offset": [
        "coalesce",
        ["get", "text_offset"],
        ["literal", [0, -1.5]]
      ],
      // Data-driven text justification - reads "text_justify" property from feature, defaults to "center"
      // Options: "auto", "left", "center", "right"
      // See: https://docs.mapbox.com/style-spec/reference/layers/#layout-symbol-text-justify
      "text-justify": ["coalesce", ["get", "text_justify"], "center"],
      // Labels must always display - allow overlap to ensure visibility
      // See: https://docs.mapbox.com/style-spec/reference/layers/#layout-symbol-text-allow-overlap
      "text-allow-overlap": true,
      // Don't ignore placement of other labels (still participates in collision detection for anchor selection)
      // See: https://docs.mapbox.com/style-spec/reference/layers/#layout-symbol-text-ignore-placement
      "text-ignore-placement": false,
      // Labels are required (always shown)
      // See: https://docs.mapbox.com/style-spec/reference/layers/#layout-symbol-text-optional
      "text-optional": false,
      // Enable multi-line text wrapping
      // See: https://docs.mapbox.com/style-spec/reference/layers/#layout-symbol-text-max-width
      "text-max-width": 10,
      // Add letter spacing for a more refined, distinctive look
      // See: https://docs.mapbox.com/style-spec/reference/layers/#layout-symbol-text-letter-spacing
      "text-letter-spacing": 0.05
    },
    paint: {
      // Warm cream/off-white color to subtly echo the orange route lines
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#paint-symbol-text-color
      "text-color": "#fff5e6",
      // Deep warm charcoal halo (softer than pure black, complements orange routes)
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#paint-symbol-text-halo-color
      "text-halo-color": "#2d2520",
      // Slightly thinner halo for a more refined appearance
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#paint-symbol-text-halo-width
      "text-halo-width": 1.5,
      // Softer blur for a gentler outline
      // See: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#paint-symbol-text-halo-blur
      "text-halo-blur": 0.5
    },
    // Filter out features with no labels
    filter: [
      "any",
      ["!=", ["coalesce", ["get", "label_line_1"], ""], ""],
      ["!=", ["coalesce", ["get", "label_line_2"], ""], ""]
    ],
    minzoom: 2
  };
}

/**
 * Create Mapbox layer specs for migration wave polygon layers (fill + outline).
 * @param {string} sourceId - The ID of the GeoJSON source
 * @param {string} fillLayerId - Unique fill layer ID (e.g., "migration-wave-1-polygon-fill")
 * @param {string} outlineLayerId - Unique outline layer ID (e.g., "migration-wave-1-polygon-outline")
 * @param {string} color - Color for fill and outline (default: "#ffa500")
 * @returns {{fill: import('mapbox-gl').AnyLayer, outline: import('mapbox-gl').AnyLayer}} Fill and outline layers
 */
export function createMigrationWavePolygonLayers(
  sourceId,
  fillLayerId,
  outlineLayerId,
  color = "#ffa500"
) {
  return {
    fill: {
      id: fillLayerId,
      type: "fill",
      source: sourceId,
      paint: {
        "fill-color": color,
        "fill-opacity": 0.2
      }
    },
    outline: {
      id: outlineLayerId,
      type: "line",
      source: sourceId,
      paint: {
        "line-color": color,
        "line-width": 2,
        "line-opacity": 0.6
      }
    }
  };
}
